/**
 * New Deal Page
 *
 * Page showing the deal creation flow
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useIsDemo } from '@/hooks/common/useIsDemo';
import { useOrg } from '@/contexts/OrgContext';
import { useDealOperations } from '@/hooks/deals/useDealOperations';
import { DealStage } from '@equaliq/api-model-v2';
import { PATH_ROOT } from '@/routes/paths';
import { DEMO_CONTRACT_IDS } from '@/mockData/demoContractData';
import { DealCreationFlow } from './components/DealCreationFlow';

export const NewDealPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDemo = useIsDemo();
  const { selectedOrgId } = useOrg();
  const { createDeal } = useDealOperations(selectedOrgId);
  const [isCreating, setIsCreating] = useState(false);

  const initialMethodParam = searchParams.get('method');
  const initialMethod =
    initialMethodParam === 'upload' ||
    initialMethodParam === 'template' ||
    initialMethodParam === 'meeting'
      ? initialMethodParam
      : null;
  const fromContractIq = searchParams.get('from') === 'contract-iq';
  const initialTemplateId = searchParams.get('templateId');

  const handleComplete = async (dealData: any) => {
    if (isDemo) {
      navigate(`${PATH_ROOT.demo.analysis}/${DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL}`);
      return;
    }

    if (!selectedOrgId || isCreating) return;
    setIsCreating(true);

    try {
      const stage = dealData.stage as DealStage | undefined;
      const result = await createDeal({
        orgId: selectedOrgId,
        title: dealData.context?.trim() ? dealData.context.slice(0, 100) : 'Untitled Deal',
        description: dealData.context || undefined,
        initialStage: stage && Object.values(DealStage).includes(stage) ? stage : DealStage.drafting,
      });

      const dealId = result.deal.dealId;
      navigate(`${PATH_ROOT.main.deal}/${dealId}`);
    } catch (err) {
      console.error('Failed to create deal:', err);
      setIsCreating(false);
    }
  };

  const handleCancel = () => {
    if (isDemo && fromContractIq) {
      navigate(`${PATH_ROOT.demo.contractIq}?view=template-library`);
      return;
    }
    navigate(isDemo ? PATH_ROOT.demo.dealPipeline : PATH_ROOT.main.dealPipeline);
  };

  return (
    <DealCreationFlow
      onComplete={handleComplete}
      onCancel={handleCancel}
      initialMethod={initialMethod}
      initialTemplateId={initialTemplateId}
      onBackToTemplateLibrary={
        isDemo && fromContractIq
          ? () => navigate(`${PATH_ROOT.demo.contractIq}?view=template-library`)
          : undefined
      }
    />
  );
};

export default NewDealPage;
